/**
 * User data hook - manages user/student data and related actions
 *
 * Handles:
 * - Header details (student info, grades, user state, feature flags)
 * - Student account details
 * - Dashboard details
 * - Avatars
 * - Password change
 * - Orientation completion
 * - Grade change OTP
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { GRADE_STATUS } from "@ts/enums/GradeStatus";
import { GradeTheme } from "@euka-mfes/bark";
import { studentAccountApi } from "@api/studentAccountApi";
import { gradeChangeApi } from "@api/gradeChangeApi";
import { changePasswordApi } from "@api/changePasswordApi";
import { orientationApi } from "@api/orientationApi";
import { getProductFamilyForGradeCode } from "@utils/productFamilyGrades";
import { logger } from "@/utils/loggingUtils";
import type { Grade, Student, ExternalService } from "@ts/interfaces/student/studentAccountInterface";
import type {
  DashboardDetailsData,
  HeaderDetailsApiResponse,
  NotificationInterface
} from "@ts/interfaces/student/api/studentAccountApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";

// ============================================
// Types
// ============================================

export interface HeaderDetails {
  student: Student | null;
  grades: Grade[];
  externalServices: ExternalService[];
  userState: { introScreenSeen: boolean };
  uploadPractiseSectionEnabled: boolean;
  uploadActivitySheetEnabled: boolean;
  chatEnabled: boolean;
  lessonAudioEnabled: boolean;
  orientationEnabled: boolean;
  lessonImageEnlargementEnabled: boolean;
  achievementsTermAndGradeEnabled: boolean;
  achievementsElectiveEnabled: boolean;
  achievementsUniversityEnabled: boolean;
  achievementsExcellenceEnabled: boolean;
  answerSheetAccessManagementEnabled: boolean;
}

export const defaultHeaderDetails: HeaderDetails = {
  student: null,
  grades: [],
  externalServices: [],
  userState: { introScreenSeen: false },
  uploadPractiseSectionEnabled: false,
  uploadActivitySheetEnabled: false,
  chatEnabled: false,
  lessonAudioEnabled: false,
  orientationEnabled: false,
  lessonImageEnlargementEnabled: false,
  achievementsTermAndGradeEnabled: true,
  achievementsElectiveEnabled: true,
  achievementsUniversityEnabled: true,
  achievementsExcellenceEnabled: true,
  answerSheetAccessManagementEnabled: false
};

/**
 * Maps raw API response to HeaderDetails structure
 */
export function mapHeaderDetails(
  payload: Partial<HeaderDetailsApiResponse>
): HeaderDetails {
  return {
    grades: (payload.grades ?? []).map(
      (g): Grade => ({
        ...(g as unknown as Omit<
          Grade,
          | "assignmentsInLearningEnabled"
          | "assignmentDetailInLearningEnabled"
          | "assignmentSubmitPanelEnabled"
          | "assignmentWorkSampleUploadsEnabled"
        >),
        assignmentsInLearningEnabled:
          (g as { assignments_in_learning_enabled?: boolean })
            .assignments_in_learning_enabled ?? false,
        assignmentDetailInLearningEnabled:
          (g as { assignment_detail_in_learning_enabled?: boolean })
            .assignment_detail_in_learning_enabled ?? false,
        assignmentSubmitPanelEnabled:
          (g as { assignment_submit_panel_enabled?: boolean })
            .assignment_submit_panel_enabled ?? false,
        assignmentWorkSampleUploadsEnabled:
          (g as { assignment_work_sample_uploads_enabled?: boolean })
            .assignment_work_sample_uploads_enabled ?? false
      })
    ),
    student: (payload.student as Student) ?? null,
    externalServices: (payload.external_services as ExternalService[]) ?? [],
    userState: {
      introScreenSeen: (payload.user_state as { intro_screen_seen?: boolean })?.intro_screen_seen ?? false
    },
    uploadPractiseSectionEnabled: (payload.practise_section_upload_enabled as boolean) ?? false,
    uploadActivitySheetEnabled: (payload.activity_sheet_upload_enabled as boolean) ?? false,
    chatEnabled: (payload.chat_enabled as boolean) ?? false,
    lessonAudioEnabled: (payload.lesson_audio_enabled as boolean) ?? false,
    orientationEnabled: (payload.orientation_enabled as boolean) ?? false,
    lessonImageEnlargementEnabled: (payload.lesson_image_enlargement_enabled as boolean) ?? false,
    achievementsTermAndGradeEnabled: (payload.achievements_term_and_grade_enabled as boolean) ?? true,
    achievementsElectiveEnabled: (payload.achievements_elective_enabled as boolean) ?? true,
    achievementsUniversityEnabled: (payload.achievements_university_enabled as boolean) ?? true,
    achievementsExcellenceEnabled: (payload.achievements_excellence_enabled as boolean) ?? true,
    answerSheetAccessManagementEnabled: (payload.answer_sheet_access_management_enabled as boolean) ?? false
  };
}

interface UseUserDataParams {
  urlGradeCode: number | undefined;
}

export interface UseUserDataReturn {
  // Header/User data
  headerDetails: HeaderDetails;
  headerDetailsStatus: REQUEST_STATUS;
  studentsFamilyAvatars: number[];
  dashboardDetails: DashboardDetailsData | null;

  // Grade (derived from URL + headerDetails)
  selectedGrade: Grade | null;
  gradeTheme: GradeTheme;

  // Grade activation state
  activatingGradeCode: number | null;
  setActivatingGradeCode: (code: number | null) => void;
  changedGrade: Grade | null;
  setChangedGrade: (grade: Grade | null) => void;

  // Actions
  fetchHeaderDetails: () => Promise<void>;
  /** Fetch family avatars if not already loaded. No-op if already fetched or in-flight. */
  ensureFamilyAvatars: () => void;
  /** Force re-fetch family avatars (e.g. error retry). */
  fetchFamilyAvatars: () => void;
  updateAvatar: (avatarKey: number) => Promise<{ success: boolean }>;
  changePassword: (data: { current_password: string; new_password: string; new_password_confirmation: string }) => Promise<{ success: boolean }>;
  completeOrientation: (gradeCode: number) => Promise<{ success: boolean }>;
  updateUserState: (data: { intro_screen_seen?: boolean }) => Promise<{ success: boolean }>;
  fetchGradeChangeOtp: (gradeCode: number) => Promise<void>;
  validateGradeChangeOtp: (otp: string) => Promise<void>;

  // Request statuses
  getDashboardDetailsRequestStatus: REQUEST_STATUS;
  getGradeChangeOtpRequestStatus: REQUEST_STATUS;
  validateGradeChangeOtpRequestStatus: REQUEST_STATUS;
  updateAvatarRequestStatus: REQUEST_STATUS;
  completeOrientationRequestStatus: REQUEST_STATUS;
  changePasswordRequestStatus: REQUEST_STATUS;
  changePasswordRequestError: ErrorInterface | null;
  validateGradeChangeOtpRequestError: ErrorInterface | null;

  // Reset functions
  resetAvatarState: () => void;
  resetChangePasswordState: () => void;

  // Notification dismissal (BLD2-1087)
  dismissedNotificationIds: Set<number>;
  dismissNotification: (notificationId: number) => void;
  dismissNotificationWhere: (
    predicate: (notification: NotificationInterface) => boolean
  ) => void;
}

export function useUserData({ urlGradeCode }: UseUserDataParams): UseUserDataReturn {
  // ─────────────────────────────────────────────────────────────
  // Header/User state
  // ─────────────────────────────────────────────────────────────
  const [headerDetails, setHeaderDetails] = useState<HeaderDetails>(defaultHeaderDetails);
  const [headerDetailsStatus, setHeaderDetailsStatus] = useState<REQUEST_STATUS>(REQUEST_STATUS.IDLE);
  const [studentsFamilyAvatars, setStudentsFamilyAvatars] = useState<number[]>([]);
  const [dashboardDetails, setDashboardDetails] = useState<DashboardDetailsData | null>(null);

  // Request statuses
  const [getDashboardDetailsRequestStatus, setGetDashboardDetailsRequestStatus] = useState<REQUEST_STATUS>(REQUEST_STATUS.IDLE);
  const [getGradeChangeOtpRequestStatus, setGetGradeChangeOtpRequestStatus] = useState<REQUEST_STATUS>(REQUEST_STATUS.IDLE);
  const [validateGradeChangeOtpRequestStatus, setValidateGradeChangeOtpRequestStatus] = useState<REQUEST_STATUS>(REQUEST_STATUS.IDLE);
  const [validateGradeChangeOtpRequestError, setValidateGradeChangeOtpRequestError] = useState<ErrorInterface | null>(null);
  const [updateAvatarRequestStatus, setUpdateAvatarRequestStatus] = useState<REQUEST_STATUS>(REQUEST_STATUS.IDLE);
  const [completeOrientationRequestStatus, setCompleteOrientationRequestStatus] = useState<REQUEST_STATUS>(REQUEST_STATUS.IDLE);
  const [changePasswordRequestStatus, setChangePasswordRequestStatus] = useState<REQUEST_STATUS>(REQUEST_STATUS.IDLE);
  const [changePasswordRequestError, setChangePasswordRequestError] = useState<ErrorInterface | null>(null);

  // Grade activation state
  const [activatingGradeCode, setActivatingGradeCode] = useState<number | null>(null);
  const [changedGrade, setChangedGrade] = useState<Grade | null>(null);

  // ─────────────────────────────────────────────────────────────
  // Fetch header details on mount
  // ─────────────────────────────────────────────────────────────
  const headerFetchedRef = useRef(false);

  const fetchHeaderDetails = useCallback(async () => {
    setHeaderDetailsStatus(REQUEST_STATUS.PENDING);
    try {
      const response = await studentAccountApi.getHeaderDetails();
      setHeaderDetails(mapHeaderDetails(response));
      setHeaderDetailsStatus(REQUEST_STATUS.SUCCESS);
    } catch (error) {
      logger.warn("Error fetching header details:", error);
      setHeaderDetailsStatus(REQUEST_STATUS.ERROR);
    }
  }, []);

  useEffect(() => {
    if (headerFetchedRef.current) return;
    headerFetchedRef.current = true;
    fetchHeaderDetails();
  }, [fetchHeaderDetails]);

  // ─────────────────────────────────────────────────────────────
  // Derive selectedGrade from URL + headerDetails
  // ─────────────────────────────────────────────────────────────
  const selectedGrade = useMemo((): Grade | null => {
    if (headerDetailsStatus !== REQUEST_STATUS.SUCCESS) return null;
    if (!headerDetails.grades.length) return null;

    // If URL has a grade, use it
    if (urlGradeCode) {
      const gradeFromUrl = headerDetails.grades.find(g => g.grade_code === urlGradeCode);
      if (gradeFromUrl) return gradeFromUrl;
      // Invalid grade in URL - will be handled by access control
      return null;
    }

    // No grade in URL - find default (current or first active)
    const currentGrade = headerDetails.grades.find(g => g.status === GRADE_STATUS.CURRENT);
    if (currentGrade) return currentGrade;

    const activeGrade = headerDetails.grades.find(g => g.status !== GRADE_STATUS.NOT_STARTED);
    return activeGrade ?? null;
  }, [headerDetailsStatus, headerDetails.grades, urlGradeCode]);

  // Derive gradeTheme from urlGradeCode so the theme is correct on first paint.
  // Fallback to selectedGrade for routes without a grade in the URL (e.g.
  // /learning landing page).
  const gradeTheme = useMemo((): GradeTheme => {
    const activeGradeCode = urlGradeCode ?? selectedGrade?.grade_code;
    if (!activeGradeCode) return GradeTheme.Primary;
    return (getProductFamilyForGradeCode(activeGradeCode) as GradeTheme | undefined)
      ?? GradeTheme.Primary;
  }, [urlGradeCode, selectedGrade]);

  // ─────────────────────────────────────────────────────────────
  // Fetch dashboard details once per grade. Lesson-driven data now comes from
  // /progress and /assignments, so dashboard-details is static for a session
  // and is never re-invalidated on a lesson action. BLD2-1113.
  // ─────────────────────────────────────────────────────────────
  const lastFetchedDashboardGradeRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!urlGradeCode) {
      setDashboardDetails(null);
      setGetDashboardDetailsRequestStatus(REQUEST_STATUS.IDLE);
      lastFetchedDashboardGradeRef.current = undefined;
      return;
    }

    if (lastFetchedDashboardGradeRef.current === urlGradeCode) {
      return;
    }

    lastFetchedDashboardGradeRef.current = urlGradeCode;
    setGetDashboardDetailsRequestStatus(REQUEST_STATUS.PENDING);

    studentAccountApi
      .getDashboardDetails(urlGradeCode)
      .then(response => {
        // Guard against stale responses when grade changed mid-flight
        if (lastFetchedDashboardGradeRef.current !== urlGradeCode) return;
        setDashboardDetails(response.data);
        setGetDashboardDetailsRequestStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        // Guard against stale responses when grade changed mid-flight
        if (lastFetchedDashboardGradeRef.current !== urlGradeCode) return;
        logger.warn("Error fetching dashboard details:", error);
        setGetDashboardDetailsRequestStatus(REQUEST_STATUS.ERROR);
        lastFetchedDashboardGradeRef.current = undefined;
      });
  }, [urlGradeCode]);

  // ─────────────────────────────────────────────────────────────
  // Lazy-fetch family avatars (views call ensureFamilyAvatars on mount)
  // ─────────────────────────────────────────────────────────────
  const [, setFamilyAvatarsStatus] = useState<REQUEST_STATUS>(REQUEST_STATUS.IDLE);

  const fetchFamilyAvatars = useCallback(() => {
    setFamilyAvatarsStatus(REQUEST_STATUS.PENDING);
    studentAccountApi
      .getStudentsFamilyUsedAvatars()
      .then(response => {
        setStudentsFamilyAvatars(response.selected_avatars ?? []);
        setFamilyAvatarsStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        logger.warn("Error fetching family avatars:", error);
        setFamilyAvatarsStatus(REQUEST_STATUS.ERROR);
      });
  }, []);

  /** Fetch once — no-op if already fetched or in-flight. */
  const ensureFamilyAvatars = useCallback(() => {
    setFamilyAvatarsStatus(prev => {
      if (prev !== REQUEST_STATUS.IDLE) return prev; // already attempted
      // Kick off the fetch (runs after this render via microtask)
      queueMicrotask(fetchFamilyAvatars);
      return REQUEST_STATUS.PENDING;
    });
  }, [fetchFamilyAvatars]);

  const updateAvatar = useCallback(
    async (avatarKey: number): Promise<{ success: boolean }> => {
      // Capture the real previous avatar from state (not the closure) so
      // rollback is accurate even if headerDetails changed since mount.
      let previousAvatarKey = 1;

      // Optimistic update BEFORE API call for immediate UI feedback
      setHeaderDetails(prev => {
        previousAvatarKey = prev.student?.avatar_key ?? 1;
        return {
          ...prev,
          student: prev.student ? { ...prev.student, avatar_key: avatarKey } : null
        };
      });
      setUpdateAvatarRequestStatus(REQUEST_STATUS.PENDING);

      try {
        await studentAccountApi.updateAvatar({ content: { avatar_key: avatarKey } });
        setUpdateAvatarRequestStatus(REQUEST_STATUS.SUCCESS);
        return { success: true };
      } catch (error) {
        logger.warn("Error updating avatar:", error);
        // Rollback optimistic update on error
        setHeaderDetails(prev => ({
          ...prev,
          student: prev.student ? { ...prev.student, avatar_key: previousAvatarKey } : null
        }));
        setUpdateAvatarRequestStatus(REQUEST_STATUS.ERROR);
        throw error;
      }
    },
    []
  );

  const changePassword = useCallback(
    async (data: { current_password: string; new_password: string; new_password_confirmation: string }): Promise<{ success: boolean }> => {
      setChangePasswordRequestStatus(REQUEST_STATUS.PENDING);
      setChangePasswordRequestError(null);
      try {
        await changePasswordApi.updatePassword(data);
        try {
          const headerDetailsResponse = await studentAccountApi.getHeaderDetails();
          setHeaderDetails(mapHeaderDetails(headerDetailsResponse));
        } catch (refreshError) {
          logger.warn(
            "Error refreshing header details after password change:",
            refreshError
          );
        }
        setChangePasswordRequestStatus(REQUEST_STATUS.SUCCESS);
        return { success: true };
      } catch (error: unknown) {
        const err = error as { customAttributes?: { httpErrorStatus?: number; httpErrorResponseData?: string } };
        const parsed = JSON.parse(err?.customAttributes?.httpErrorResponseData ?? "{}");
        setChangePasswordRequestError({
          code: err?.customAttributes?.httpErrorStatus ?? 0,
          message: parsed.message ?? "Failed to update password."
        });
        setChangePasswordRequestStatus(REQUEST_STATUS.ERROR);
        throw error;
      }
    },
    []
  );

  const completeOrientation = useCallback(
    async (gradeCode: number): Promise<{ success: boolean }> => {
      setCompleteOrientationRequestStatus(REQUEST_STATUS.PENDING);
      try {
        await orientationApi.completeOrientation(gradeCode);
        setCompleteOrientationRequestStatus(REQUEST_STATUS.SUCCESS);
        // Update local state
        setHeaderDetails(prev => ({
          ...prev,
          grades: prev.grades.map(g =>
            g.grade_code === gradeCode ? { ...g, orientation_completed: true } : g
          )
        }));
        return { success: true };
      } catch (error) {
        logger.warn("Error completing orientation:", error);
        setCompleteOrientationRequestStatus(REQUEST_STATUS.ERROR);
        throw error;
      }
    },
    []
  );

  const updateUserState = useCallback(
    async (data: { intro_screen_seen?: boolean }): Promise<{ success: boolean }> => {
      try {
        if (data.intro_screen_seen !== undefined) {
          await studentAccountApi.updateUserState({
            key: "intro_screen_seen",
            value: data.intro_screen_seen
          });
          setHeaderDetails(prev => ({
            ...prev,
            userState: { ...prev.userState, introScreenSeen: data.intro_screen_seen ?? false }
          }));
        }
        return { success: true };
      } catch (error) {
        logger.warn("Error updating user state:", error);
        throw error;
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────
  // Actions - Grade change
  // ─────────────────────────────────────────────────────────────
  const fetchGradeChangeOtp = useCallback(async (gradeCode: number) => {
    setGetGradeChangeOtpRequestStatus(REQUEST_STATUS.PENDING);
    try {
      await gradeChangeApi.generateOTP(gradeCode);
      setGetGradeChangeOtpRequestStatus(REQUEST_STATUS.SUCCESS);
    } catch (error) {
      logger.warn("Error generating OTP:", error);
      setGetGradeChangeOtpRequestStatus(REQUEST_STATUS.ERROR);
      throw error;
    }
  }, []);

  const validateGradeChangeOtp = useCallback(
    async (otp: string): Promise<void> => {
      setValidateGradeChangeOtpRequestStatus(REQUEST_STATUS.PENDING);
      setValidateGradeChangeOtpRequestError(null);
      try {
        await gradeChangeApi.validateOTP(otp);
        setValidateGradeChangeOtpRequestStatus(REQUEST_STATUS.SUCCESS);
      } catch (error: unknown) {
        const err = error as { customAttributes?: { httpErrorStatus?: number; httpErrorResponseData?: string } };
        const parsed = JSON.parse(err?.customAttributes?.httpErrorResponseData ?? "{}");
        setValidateGradeChangeOtpRequestError({
          code: err?.customAttributes?.httpErrorStatus ?? 0,
          message: parsed.message ?? "Failed to validate OTP."
        });
        setValidateGradeChangeOtpRequestStatus(REQUEST_STATUS.ERROR);
        throw error;
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────
  // Reset functions
  // ─────────────────────────────────────────────────────────────
  const resetAvatarState = useCallback(() => {
    setUpdateAvatarRequestStatus(REQUEST_STATUS.IDLE);
    setCompleteOrientationRequestStatus(REQUEST_STATUS.IDLE);
  }, []);

  const resetChangePasswordState = useCallback(() => {
    setChangePasswordRequestStatus(REQUEST_STATUS.IDLE);
    setChangePasswordRequestError(null);
  }, []);

  // BLD2-1087: dismissed-notification tracking at context level so dismissals
  // survive dashboard remount after CTA navigation.
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState<
    Set<number>
  >(new Set());

  useEffect(() => {
    setDismissedNotificationIds(new Set());
  }, [urlGradeCode]);

  const dismissNotification = useCallback((notificationId: number) => {
    setDismissedNotificationIds(prev => {
      if (prev.has(notificationId)) return prev;
      const next = new Set(prev);
      next.add(notificationId);
      return next;
    });

    studentAccountApi.markNotificationAsRead(notificationId).catch(error => {
      logger.warn("Failed to mark notification as read:", error);
    });
  }, []);

  const dismissNotificationWhere = useCallback(
    (predicate: (notification: NotificationInterface) => boolean) => {
      setDashboardDetails(prev => {
        if (!prev) return prev;
        const notifications = prev.notifications ?? [];
        const next = notifications.filter(n => !predicate(n));
        if (next.length === notifications.length) return prev;
        return { ...prev, notifications: next };
      });
    },
    []
  );

  return {
    // Header/User data
    headerDetails,
    headerDetailsStatus,
    studentsFamilyAvatars,
    dashboardDetails,
    dismissedNotificationIds,
    dismissNotification,

    // Grade
    selectedGrade,
    gradeTheme,
    activatingGradeCode,
    setActivatingGradeCode,
    changedGrade,
    setChangedGrade,

    // Actions
    fetchHeaderDetails,
    ensureFamilyAvatars,
    fetchFamilyAvatars,
    updateAvatar,
    changePassword,
    completeOrientation,
    updateUserState,
    fetchGradeChangeOtp,
    validateGradeChangeOtp,

    // Request statuses
    getDashboardDetailsRequestStatus,
    getGradeChangeOtpRequestStatus,
    validateGradeChangeOtpRequestStatus,
    updateAvatarRequestStatus,
    completeOrientationRequestStatus,
    changePasswordRequestStatus,
    changePasswordRequestError,
    validateGradeChangeOtpRequestError,

    // Reset functions
    resetAvatarState,
    resetChangePasswordState,
    dismissNotificationWhere
  };
}
